import { saveSearchToHistory } from '../../components/SearchBar/previousSearchData';
import { AbortAPIError } from '../../utils/newFetch/HTTPClient';
import LinkedEventsAPI from '../../utils/newFetch/LinkedEventsAPI';
import ServiceMapAPI from '../../utils/newFetch/ServiceMapAPI';
import { isEmbed } from '../../utils/path';
import optionsToSearchQuery from '../../utils/search';
import { getLocaleString } from '../selectors/locale';
import { selectSearchResults } from '../selectors/results';
import { getLocale } from '../selectors/user';
import { searchResults } from './fetchDataActions';

// Actions
const { isFetching, fetchSuccess, fetchProgressUpdateConcurrent, fetchError } =
  searchResults;

let activeSearchController = null;
let latestSearchRequestId = 0;

const smFetch = async (
  dispatch,
  getState,
  options,
  requestId,
  abortController
) => {
  let results = [];
  const smAPI = new ServiceMapAPI();
  if (typeof smAPI.setAbortController === 'function') {
    smAPI.setAbortController(abortController);
  }

  const onProgressUpdateConcurrent = (total, max) => {
    if (requestId !== latestSearchRequestId) {
      return;
    }
    const currentCount = selectSearchResults(getState())?.count || 0;
    if (total < currentCount) {
      return;
    }
    dispatch(fetchProgressUpdateConcurrent(total, max));
  };

  if (options.q) {
    // General text search
    const { q, ...additionalOptions } = options;
    smAPI.setOnProgressUpdate(onProgressUpdateConcurrent);
    results = smAPI.search(q, additionalOptions);
  } else if (options.service_id) {
    // Service fetch
    const { service_id, ...additionalOptions } = options;
    smAPI.setOnProgressUpdate(onProgressUpdateConcurrent);
    results = smAPI.serviceUnitSearch(service_id, additionalOptions);
  } else if (options.service_node) {
    // Service  node fetch
    const { service_node, ...additionalOptions } = options;
    smAPI.setOnProgressUpdate(onProgressUpdateConcurrent);
    results = smAPI.serviceNodeSearch(
      'ServiceTree',
      service_node,
      additionalOptions
    );
  } else if (options.mobility_node) {
    // Mobility node fetch
    const { mobility_node, ...additionalOptions } = options;
    smAPI.setOnProgressUpdate(onProgressUpdateConcurrent);
    results = smAPI.serviceNodeSearch(
      'MobilityTree',
      mobility_node,
      additionalOptions
    );
  } else if (options.address) {
    // Search units and addresses with address
    const { address, ...additionalOptions } = options;
    // Fetch units and addresses from two different endpoints
    const addressFetchOptions = { type: 'address', ...additionalOptions };
    const unitFetchOptions = { address, ...additionalOptions };
    results = Promise.all([
      smAPI.search(address, addressFetchOptions, true),
      smAPI.units(unitFetchOptions),
    ]);
  } else if (options.id) {
    const unitFetchOptions = { id: options.id };
    results = smAPI.units(unitFetchOptions);
  } else if (options.events) {
    const eventsAPI = new LinkedEventsAPI();
    if (typeof eventsAPI.setAbortController === 'function') {
      eventsAPI.setAbortController(abortController);
    }
    results = eventsAPI.eventsByKeyword(options.events);
  } else if (options.level) {
    results = smAPI.units(options);
  }

  return results;
};

const fetchSearchResults =
  (options = null) =>
  async (dispatch, getState) => {
    const locale = getLocale(getState());

    const searchQuery = optionsToSearchQuery(options);

    // Cancel previous in-flight search to avoid stale progress/success updates
    // racing with the latest search results.
    if (activeSearchController) {
      activeSearchController.abort();
    }
    const requestId = latestSearchRequestId + 1;
    latestSearchRequestId = requestId;
    const abortController = new AbortController();
    activeSearchController = abortController;

    dispatch(isFetching(searchQuery));

    const extraFields = [
      'unit.connections',
      'unit.phone',
      'unit.call_charge_info',
      'unit.email',
      'unit.www',
      'unit.address_zip',
    ];
    const fetchOptions = {
      ...options,
      language: locale,
      ...(isEmbed() ? { include: extraFields } : {}),
    };
    let results;
    try {
      results = await smFetch(
        dispatch,
        getState,
        fetchOptions,
        requestId,
        abortController
      );
    } catch (e) {
      // Only silently handle genuine abort errors (user navigated away or 10s timeout
      // fired). Other APIFetchErrors — missing base URL, invalid input, etc. — should
      // still surface so they are visible in Sentry.
      if (!(e instanceof AbortAPIError)) throw e;
      // Only reset fetch state if this is still the latest request.
      if (requestId === latestSearchRequestId) {
        dispatch(fetchError(null));
      }
      return;
    } finally {
      if (
        requestId === latestSearchRequestId &&
        activeSearchController === abortController
      ) {
        activeSearchController = null;
      }
    }

    if (requestId !== latestSearchRequestId) {
      return;
    }

    /* Handle search results */

    if (results?.length) {
      // Handle text search results
      if (options.q) {
        saveSearchToHistory(searchQuery, {
          object_type: 'searchHistory',
          text: searchQuery,
        });
      }
      // Handle unit results that have no object_type
      const keys = [
        'service_node',
        'mobility_node',
        'service_id',
        'id',
        'level',
      ];
      if (keys.some((key) => !!options[key])) {
        results.forEach((item) => {
          item.object_type = 'unit';
        });
      }
      // Handle "search with address" results
      if (options.address) {
        // If address has a number specified, we can assume it is exact address instead of street
        const isExactAddress = /\d/.test(options.address);
        let addressData = results[0];
        const unitData = results[1];
        unitData.forEach((unit) => {
          unit.object_type = 'unit';
        });
        if (isExactAddress) {
          addressData = addressData.filter(
            (res) => getLocaleString(locale, res.name) === options.address
          );
        }
        results = [...addressData, ...unitData];
      }
      // Handle event search results
      if (options.events) {
        results.forEach((event) => {
          event.object_type = 'event';
          const eventUnit = event.location;
          if (eventUnit) {
            eventUnit.object_type = 'unit';
            if (typeof eventUnit.id === 'string') {
              eventUnit.id = Number.parseInt(eventUnit.id.match(/[0-9]+/g), 10);
            }
          }
        });
      }
    }

    // A newer search may have started while we were normalizing results.
    // Prevent stale payload from replacing fresh search results.
    if (requestId !== latestSearchRequestId) {
      return;
    }

    dispatch(fetchSuccess(results));
  };

export default fetchSearchResults;
